/**
 * A business account — a shop, café, restaurant or event venue — signs up only
 * to collect customer feedback, so it gets a slice of the platform an
 * organization sees: its workspaces, the projects inside them, a project's
 * dashboard, customer feedback surveys (create, edit, share, preview), the
 * automated campaigns that send them out and the survey analytics of what
 * those surveys collected. Standard surveys, templates, audiences and
 * assignments are an organization's tools and stay hidden.
 *
 * The sign-up side of the same split lives in pages/auth/register/accountLevel.js.
 */

import AuthService from "./authentication.service";

/** The level an organization is registered under, as the API reports it. */
export const OrganizationLevel = Object.freeze({
   Organization: 0,
   Business: 1
});

// The organization the user is signed in to, out of the list the login response
// stored. A user with a single organization has it selected already; the
// fallback covers the moment right after login, before that selection lands.
const getCurrentOrganization = () => {
   const organizations = AuthService.getUser()?.organizations ?? [];
   const organizationId = AuthService.getOrganizationId();

   return organizations.find((org) => org.organizationId === organizationId) ?? organizations[0];
};

/**
 * The level the signed-in account is registered under. An organization the API
 * reports no level for is an organization, the level everything predating the
 * business sign up was created with.
 */
export const getCurrentOrganizationLevel = () =>
   getCurrentOrganization()?.organizationLevel ?? OrganizationLevel.Organization;

/** True when the signed-in account registered as retail, food or events. */
export const isBusinessAccount = () => getCurrentOrganizationLevel() === OrganizationLevel.Business;

// Everything a business account may open, by path prefix. Anything outside this
// list — a standard survey builder, the audience, a deep link someone shared —
// sends it back to its surveys (see hooks/useBusinessAccountRedirect.js).
const BUSINESS_ALLOWED_PATHS = [
   // Public and unauthenticated pages, which the guard must never touch.
   "/public-survey",
   "/auth",
   "/unauthorized",
   "/coming-soon",
   "/404",
   "/500",
   // Workspaces, projects and the project dashboard.
   "/organizations",
   "/admin-workspaces",
   "/admin-deleted-items",
   "/workspace",
   "/space-selection",
   "/project-selection",
   "/projects",
   "/dashboard",
   // Surveys: the list, and the customer feedback mode alone. A feedback survey
   // is a real survey record, so its details are edited on the shared page.
   "/surveys",
   "/survey/feedback",
   "/survey/edit-survey-details",
   // One submission, opened from the Survey Results table. Read-only, and the
   // same page an organization opens for any survey's submission.
   "/survey/submission",
   // Survey analytics: a feedback survey's submissions are the point of
   // collecting them, so the analytics page and its per-survey deep links are
   // read the same way an organization reads them.
   "/survey-analytics",
   // Automated campaigns, the same pages an organization uses. The API only
   // offers (and only accepts) a business account's customer feedback surveys
   // there (Campaign/getSurveysForCampaign).
   "/automated-campaigns",
   // The billing page an account held by the new billing flow lands on, and the
   // page Stripe hands a card back to. Without these the two redirects — this
   // one and the billing one — would send each other round in a circle.
   "/billing",
   // Account settings.
   "/profile",
   "/login-information",
   "/subscription-plans",
   "/usage",
   "/balance",
   "/tickets"
];

export const isBusinessAllowedPath = (pathname) => {
   const path = pathname?.replace(/\/+$/, "") || "/";

   // The root route only redirects onwards, by role, so it is always reachable.
   if (path === "/") return true;

   return BUSINESS_ALLOWED_PATHS.some(
      (allowed) => path === allowed || path.startsWith(`${allowed}/`)
   );
};
